/**
 * Shared metadata-icon hook.
 *
 * Fetches brand + platform icons ONCE per page load and caches them in a
 * module-level map. Consumers get a synchronous lookup:
 *   const { brandIcon, platformIcon } = useMetadataIcons();
 *   <img src={brandIcon('Osmo') || fallback} />
 *
 * We deliberately do a single fetch on mount and share the promise across
 * consumers to avoid N parallel fetches when many components use the hook.
 */
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Module-level cache — shared across every consumer on the page.
let _cache = null;
let _promise = null;

async function _fetchAll() {
  if (_cache) return _cache;
  if (_promise) return _promise;
  _promise = (async () => {
    try {
      const [b, p] = await Promise.all([
        fetch(`${API}/control-panel/brands`, { credentials: 'include' }).then(r => r.ok ? r.json() : { brands: [] }),
        fetch(`${API}/control-panel/platforms`, { credentials: 'include' }).then(r => r.ok ? r.json() : { platforms: [] }),
      ]);
      const brandMap = {};
      for (const x of (b.brands || [])) brandMap[String(x.name).toLowerCase()] = x.icon_url || '';
      const platformMap = {};
      for (const x of (p.platforms || [])) platformMap[String(x.name).toLowerCase()] = x.icon_url || '';
      _cache = { brandMap, platformMap };
      return _cache;
    } catch {
      _cache = { brandMap: {}, platformMap: {} };
      return _cache;
    } finally {
      _promise = null;
    }
  })();
  return _promise;
}

export function invalidateMetadataIconCache() { _cache = null; }

export function useMetadataIcons() {
  const [state, setState] = useState(_cache || { brandMap: {}, platformMap: {} });
  useEffect(() => {
    let cancelled = false;
    _fetchAll().then(d => { if (!cancelled) setState(d); });
    return () => { cancelled = true; };
  }, []);
  const brandIcon = (name) => state.brandMap[String(name || '').toLowerCase()] || '';
  const platformIcon = (name) => state.platformMap[String(name || '').toLowerCase()] || '';
  return { brandIcon, platformIcon };
}
